import React, { useEffect, useMemo, useRef, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/src/style.css";
import "../styles/SingleDatePicker.css";

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
function getDigitIndexFromCaret(maskedValue, caretPosition) {
  let digitIndex = 0;

  for (let i = 0; i < caretPosition; i += 1) {
    if (/\d/.test(maskedValue[i])) {
      digitIndex += 1;
    }
  }

  return digitIndex;
}

function setCaretPosition(input, pos) {
  requestAnimationFrame(() => {
    input.setSelectionRange(pos, pos);
  });
}
function parseDDMMYYYY(value) {
  if (!value || typeof value !== "string") return undefined;
  const parts = value.split("/");
  if (parts.length !== 3) return undefined;
  const [day, month, year] = parts.map(Number);
  if (!day || !month || !year) return undefined;
  const result = new Date(year, month - 1, day);
  if (Number.isNaN(result.getTime())) return undefined;
  return result;
}
function formatMaskedDateFromDigits(rawDigits) {
  const digits = (rawDigits || "").replace(/\D/g, "").slice(0, 8);

  let month = "MM";
  let day = "DD";
  let year = "YYYY";

  let rest = digits;

  // Month
  if (rest.length >= 1) {
    const first = rest[0];

    if (/[2-9]/.test(first)) {
      month = `0${first}`;
      rest = rest.slice(1);
    } else if (rest.length >= 2) {
      const candidate = rest.slice(0, 2);
      const monthNum = Number(candidate);

      if (monthNum >= 1 && monthNum <= 12) {
        month = candidate;
        rest = rest.slice(2);
      } else {
        month = `0${first}`;
        rest = rest.slice(1);
      }
    } else if (/[0-1]/.test(first)) {
      month = first === "0" ? "0" : first;
      rest = rest.slice(1);
    }
  }

  // Day
  if (rest.length >= 1) {
    const first = rest[0];

    if (/[4-9]/.test(first)) {
      day = `0${first}`;
      rest = rest.slice(1);
    } else if (rest.length >= 2) {
      const candidate = rest.slice(0, 2);
      const dayNum = Number(candidate);

      if (dayNum >= 1 && dayNum <= 31) {
        day = candidate;
        rest = rest.slice(2);
      } else {
        day = `0${first}`;
        rest = rest.slice(1);
      }
    } else if (/[0-3]/.test(first)) {
      day = first;
      rest = "";
    }
  }

  // Year
  if (rest.length > 0) {
    year = `${rest}${"YYYY".slice(rest.length)}`;
  }

  return `${month}/${day}/${year}`;
}
function dateStringToDigits(value) {
  if (!value || typeof value !== "string") return "";
  return value.replace(/\D/g, "").slice(0, 8);
}
function toDDMMYYYY(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
}

function toMMDDYYYY(date) {
  if (!date || Number.isNaN(date.getTime())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

function isValidDateParts(day, month, year) {
  if (!day || !month || !year) return false;
  const candidate = new Date(year, month - 1, day);
  return (
    candidate.getFullYear() === year &&
    candidate.getMonth() === month - 1 &&
    candidate.getDate() === day
  );
}

function parseTypedDate(value) {
  if (!value || typeof value !== "string") return undefined;

  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!match) return undefined;

  const month = Number(match[1]);
  const day = Number(match[2]);
  const year = Number(match[3]);

  if (!isValidDateParts(day, month, year)) return undefined;
  return new Date(year, month - 1, day);
}

function formatDateInput(rawValue) {
  const digits = rawValue.replace(/\D/g, "").slice(0, 8);

  if (!digits) return "";

  // 1 digit entered:
  // if 2-9 => auto convert to 02/, 03/, ...
  // if 0 or 1 => wait for second month digit
  if (digits.length === 1) {
    const m1 = digits[0];

    if (/[2-9]/.test(m1)) {
      return `0${m1}/`;
    }

    if (/[0-1]/.test(m1)) {
      return m1;
    }

    return "";
  }

  // Build normalized month digits
  let normalized = digits;

  // If first digit is 2-9, auto-pad month with 0
  if (/[2-9]/.test(normalized[0])) {
    normalized = `0${normalized}`;
  }

  const monthDigits = normalized.slice(0, 2);
  const month = Number(monthDigits);

  if (month < 1 || month > 12) {
    return normalized[0] === "0" ? "0" : "";
  }

  if (normalized.length === 2) {
    return `${monthDigits}/`;
  }

  // Day first digit
  const remainingAfterMonth = normalized.slice(2);

  if (remainingAfterMonth.length === 1) {
    const d1 = remainingAfterMonth[0];

    // auto-pad day if first day digit is 4-9
    if (/[4-9]/.test(d1)) {
      return `${monthDigits}/0${d1}/`;
    }

    if (/[0-3]/.test(d1)) {
      return `${monthDigits}/${d1}`;
    }

    return `${monthDigits}/`;
  }

  // Day full
  let dayDigits = remainingAfterMonth.slice(0, 2);

  // If day starts with 4-9, it would already have been autopadded above,
  // but keep this safe in case of pasted input
  if (/[4-9]/.test(dayDigits[0])) {
    dayDigits = `0${dayDigits[0]}`;
    normalized = `${monthDigits}${dayDigits}${remainingAfterMonth.slice(1)}`;
  }

  const day = Number(dayDigits);

  if (day < 1 || day > 31) {
    return `${monthDigits}/${dayDigits[0] || ""}`;
  }

  let result = `${monthDigits}/${dayDigits}/`;

  // Year
  const yearDigits = normalized.slice(4, 8);
  if (yearDigits) {
    result += yearDigits;
  }

  return result;
}

export default function SingleDatePicker({
  value,
  onChange,
  placeholder = "MM/DD/YYYY",
  disabled = false,
  className = "",
}) {
  const [open, setOpen] = useState(false);
  const [inputDigits, setInputDigits] = useState("");
  const [showYearMenu, setShowYearMenu] = useState(false);
  const [displayMonth, setDisplayMonth] = useState(() => new Date());
  const wrapRef = useRef(null);

  const selectedDate = useMemo(() => parseDDMMYYYY(value), [value]);
  const thisYear = new Date().getFullYear();
  const years = useMemo(() => {
    const values = [];
    for (let year = thisYear + 1; year >= 1900; year -= 1) {
      values.push(year);
    }
    return values;
  }, [thisYear]);

  useEffect(() => {
    setInputDigits(
      selectedDate ? dateStringToDigits(toMMDDYYYY(selectedDate)) : "",
    );
  }, [selectedDate]);
  useEffect(() => {
    if (selectedDate) {
      setDisplayMonth(
        new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1),
      );
    }
  }, [selectedDate]);

  useEffect(() => {
    if (!open) return;

    const onOutsideClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const onEsc = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onOutsideClick);
    document.addEventListener("keydown", onEsc);

    return () => {
      document.removeEventListener("mousedown", onOutsideClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const handleSelect = (date) => {
    onChange?.(date ? toDDMMYYYY(date) : "");
    setInputDigits(date ? dateStringToDigits(toMMDDYYYY(date)) : "");
    if (date) {
      setDisplayMonth(new Date(date.getFullYear(), date.getMonth(), 1));
    }
    setOpen(false);
    setShowYearMenu(false);
  };

  const commitTypedValue = () => {
    if (!inputDigits.trim()) {
      onChange?.("");
      return;
    }

    if (inputDigits.length !== 8) {
      setInputDigits(
        selectedDate ? dateStringToDigits(toMMDDYYYY(selectedDate)) : "",
      );
      return;
    }

    const formatted = formatMaskedDateFromDigits(inputDigits);
    const parsed = parseTypedDate(formatted);

    if (!parsed) {
      setInputDigits(
        selectedDate ? dateStringToDigits(toMMDDYYYY(selectedDate)) : "",
      );
      return;
    }

    onChange?.(toDDMMYYYY(parsed));
    setInputDigits(dateStringToDigits(toMMDDYYYY(parsed)));
  };

  const handleInputChange = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
    setInputDigits(digits);
    if (!digits) {
      onChange?.("");
    }
  };

  const shiftMonth = (offset) => {
    setDisplayMonth((prev) => {
      const next = new Date(prev.getFullYear(), prev.getMonth() + offset, 1);
      const min = new Date(1900, 0, 1);
      const max = new Date(thisYear + 1, 11, 1);
      if (next < min) return min;
      if (next > max) return max;
      return next;
    });
  };

  const handleYearSelect = (year) => {
    setDisplayMonth((prev) => new Date(year, prev.getMonth(), 1));
    setShowYearMenu(false);
  };

  const inputRef = useRef(null);
  return (
    <div className={`sdp ${className}`.trim()} ref={wrapRef}>
      <div className="sdp-field">
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          className="sdp-input"
          value={formatMaskedDateFromDigits(inputDigits)}
          placeholder={placeholder}
          onChange={handleInputChange}
          onBlur={commitTypedValue}
          onFocus={() => {
            setOpen(true);
            setShowYearMenu(false);
          }}
          onKeyDown={(e) => {
            const input = e.target;
            const maskedValue = formatMaskedDateFromDigits(inputDigits);
            const caretStart = input.selectionStart ?? 0;
            const caretEnd = input.selectionEnd ?? 0;

            if (e.key === "Enter") {
              e.preventDefault();
              commitTypedValue();
              return;
            }

            if (e.key === "Backspace") {
              e.preventDefault();

              if (!inputDigits.length) return;

              const startDigitIndex = getDigitIndexFromCaret(
                maskedValue,
                caretStart,
              );
              const endDigitIndex = getDigitIndexFromCaret(
                maskedValue,
                caretEnd,
              );

              let nextDigits = inputDigits;
              let nextCaret = caretStart;

              if (caretStart !== caretEnd) {
                nextDigits =
                  inputDigits.slice(0, startDigitIndex) +
                  inputDigits.slice(endDigitIndex);
                nextCaret = caretStart;
              } else if (startDigitIndex > 0) {
                nextDigits =
                  inputDigits.slice(0, startDigitIndex - 1) +
                  inputDigits.slice(startDigitIndex);
                nextCaret = caretStart - 1;
              }

              setInputDigits(nextDigits);
              if (!nextDigits) {
                onChange?.("");
              }
              setCaretPosition(input, Math.max(0, nextCaret));
              return;
            }

            if (e.key === "Delete") {
              e.preventDefault();

              if (!inputDigits.length) return;

              // If a full selected date is present, allow single-key clear.
              if (caretStart === caretEnd && inputDigits.length === 8) {
                setInputDigits("");
                onChange?.("");
                return;
              }

              const startDigitIndex = getDigitIndexFromCaret(
                maskedValue,
                caretStart,
              );
              const endDigitIndex = getDigitIndexFromCaret(
                maskedValue,
                caretEnd,
              );

              let nextDigits = inputDigits;

              if (caretStart !== caretEnd) {
                nextDigits =
                  inputDigits.slice(0, startDigitIndex) +
                  inputDigits.slice(endDigitIndex);
              } else {
                nextDigits =
                  inputDigits.slice(0, startDigitIndex) +
                  inputDigits.slice(startDigitIndex + 1);
              }

              setInputDigits(nextDigits);
              if (!nextDigits) {
                onChange?.("");
              }
              setCaretPosition(input, caretStart);
              return;
            }
          }}
          disabled={disabled}
        />

        <button
          type="button"
          className="sdp-icon-btn"
          onClick={() => {
            if (disabled) return;
            setOpen((prev) => {
              const next = !prev;
              if (next) setShowYearMenu(false);
              return next;
            });
          }}
          disabled={disabled}
          aria-label="Open calendar"
        >
          <span className="sdp-cal-icon" aria-hidden="true" />
        </button>
      </div>

      {open && !disabled && (
        <div className="sdp-popover">
          <div className="sdp-calendar-card">
            <div className="sdp-calendar-header">
              <button
                type="button"
                className="sdp-nav-btn"
                onClick={() => shiftMonth(-1)}
                disabled={showYearMenu}
                aria-label="Previous month"
              >
                ‹
              </button>
              <div className="sdp-caption">
                <span className="sdp-month-label">
                  {MONTH_NAMES[displayMonth.getMonth()]}
                </span>
                <button
                  type="button"
                  className={`sdp-year-btn ${showYearMenu ? "active" : ""}`}
                  onClick={() => setShowYearMenu((prev) => !prev)}
                >
                  {displayMonth.getFullYear()}
                </button>
              </div>
              <button
                type="button"
                className="sdp-nav-btn"
                onClick={() => shiftMonth(1)}
                disabled={showYearMenu}
                aria-label="Next month"
              >
                ›
              </button>
            </div>

            {showYearMenu ? (
              <div className="sdp-year-menu" role="listbox" aria-label="Years">
                {years.map((year) => (
                  <button
                    key={year}
                    type="button"
                    className={`sdp-year-option ${
                      year === displayMonth.getFullYear() ? "selected" : ""
                    }`}
                    onClick={() => handleYearSelect(year)}
                  >
                    {year}
                  </button>
                ))}
              </div>
            ) : (
              <DayPicker
                mode="single"
                month={displayMonth}
                onMonthChange={(month) =>
                  setDisplayMonth(
                    new Date(month.getFullYear(), month.getMonth(), 1),
                  )
                }
                selected={selectedDate}
                onSelect={handleSelect}
                showOutsideDays
                fixedWeeks
                hideNavigation
                captionLayout="label"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
